import React, { useCallback } from 'react';

import {
	useFireAdControlAnalyticsEvents,
	useFireCrossFlowAnalyticsEvents,
} from '@atlassian/surface-analytics';
import {
	createSurfaceLoader,
	createSurfaceRenderer,
	createSurfaceSdk,
	type SurfaceSdkConfig,
} from '@atlassian/surface-sdk';
import { ExperienceSuccess, useUFOComponentExperience } from '@atlassian/ufo';

import { type Actions, type SurfaceNames } from './common/types/spec';
import { useAnalyticsSideEffect } from './controllers/analytics-side-effects';
import { createContextMiddleware } from './controllers/context-middlewares';
import { observabilitySideEffects } from './controllers/observability-side-effects';
import { type DiscoverySdk, type DiscoverySdkConfig } from './types';
import { AppRecommendations } from './ui/app-recommendations';
import { AppRecommendationsSectionHeader } from './ui/app-recommendations/app-recommendation-section-header';
import { AppRecommendationsList } from './ui/app-recommendations/app-recommendations-list';
import { AppRecommendationsWithTitle } from './ui/app-recommendations/app-recommendations-with-title';
import { showRecommendations } from './utils/ufo';

const gqlEndpoint: string = '/gateway/api/graphql';

const SurfaceRenderer = createSurfaceRenderer({
	AppRecommendationsSectionHeader,
	AppRecommendationsList,
	AppRecommendations,
	AppRecommendationsWithTitle,
});

const surfaces: SurfaceSdkConfig<SurfaceNames>['surfaces'] = {
	appSwitcher: {
		surfaceType: 'DiscoverySection',
		surfaceKey: 'app-recommendations-surface',
		contextDependencies: [
			'tenantId',
			'subproduct',
			'sessionId',
			'locale',
			'isSiteAdmin',
			'workspaceId',
			'orgId',
			{ field: 'product', required: true },
			{ field: 'switcherMenuHasBeenOpened', required: true },
		],
		fallback: null,
	},
};

const disabledSdk: DiscoverySdk = {
	DiscoverySurface: () => {
		return null;
	},
	setContext: (c) => c,
	useDiscoverySdk: () => ({ setContext: (c) => c }),
};

export const createDiscoverySdk = (config?: DiscoverySdkConfig): DiscoverySdk => {
	if (config?.enable === false) {
		return disabledSdk;
	}

	const surfaceLoader =
		config?.surfaceLoader ??
		createSurfaceLoader({
			gqlEndpoint: config?.graphqlEndpoint ?? gqlEndpoint,
		});

	const { useSurface, updateContext } = createSurfaceSdk<SurfaceNames, Actions>({
		surfaces,
		surfaceLoader,
		context: {
			...config?.context,
		},
		sideEffects: [observabilitySideEffects, ...(config?.sideEffects ?? [])],
		contextMiddleware: createContextMiddleware(),
	});

	return {
		DiscoverySurface: ({ surface, fallback, baseHeadingLevel, errorFallback }) => {
			const analyticsSideEffect = useAnalyticsSideEffect(
				useFireAdControlAnalyticsEvents(),
				useFireCrossFlowAnalyticsEvents(),
			);

			const surfaceProps = useSurface(surface, analyticsSideEffect);

			useUFOComponentExperience(showRecommendations);

			return (
				<>
					<SurfaceRenderer
						{...surfaceProps}
						fallback={fallback ?? surfaceProps.fallback}
						errorFallback={errorFallback}
						baseHeadingLevel={baseHeadingLevel}
					/>
					<ExperienceSuccess experience={showRecommendations} />
				</>
			);
		},
		setContext: updateContext,
		useDiscoverySdk: () => {
			const setContextCallback = useCallback(updateContext, []);

			return {
				setContext: setContextCallback,
			};
		},
	};
};

export type { Actions } from './common/types/spec';
